import isEqual from 'lodash/isEqual'
import moment, { Moment } from 'moment'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { BookableShipmentSavedSearchFiltersKeys } from 'apps/fleet-planner/types/bookableShipmentSavedSearch'
import { FORMATS } from 'constants/datetime'
import { MARKETPLACE_EQUIPMENT_TYPES } from 'constants/equipment'
import { IUserData } from '../context'

/**
 * Returns a haul type string based on the value of loadedMilesMax
 */
export const getHaulTypeString = (milesMax: null | number | undefined): string => {
  if (milesMax) {
    if (milesMax <= 100) return 'local'
    if (milesMax <= 300 && milesMax > 100) return 'short'
    if (milesMax > 300) return 'long'
  }
  return ''
}

/**
 * Will create a friendly formatted string for a given city, state code, and radius.
 */
export const fmtLocationStr = ({
  city,
  milesMax,
  stateCode,
}: {
  city?: null | string
  stateCode?: null | string
  milesMin?: null | number
  milesMax?: null | number
}): string | undefined => {
  const haulType = getHaulTypeString(milesMax)
  if (city && stateCode) return `${city}, ${stateCode}`
  if (stateCode) return `${stateCode}`
  if (haulType === 'short') return 'Short Hauls'
  if (haulType === 'long') return 'Long Hauls'
  if (haulType === 'local') return 'Local Hauls'
  return 'Anywhere'
}

/**
 * Will return the readable string for a given equipment id or key.
 */
export const fmtEquipmentStr = ({ id, key }: { id?: number | null; key?: string | null }) =>
  MARKETPLACE_EQUIPMENT_TYPES.find(equipment => equipment.id === id || equipment.key === key)?.name

/**
 * Will return a readable string for an ISO-8601 encoded day of week value.
 */
export const fmtDayStr = (day: number, abrev?: boolean): string | undefined =>
  moment()
    .day(day)
    .format(abrev ? 'ddd' : 'dddd')

/**
 * Will return a readable string for a given array of ISO-8601 encoded day of week values.
 */
export const fmtDaysStr = ({
  days,
}: {
  days?: number[] | null
  startDate?: string | null
  endDate?: string | null
}): string | undefined => {
  const daysSet = new Set(days)
  const daysAll = new Set([1, 2, 3, 4, 5, 6, 7])
  if (isEqual(daysSet, daysAll)) {
    return 'any day'
  }
  const daysWeek = new Set([1, 2, 3, 4, 5])
  if (isEqual(daysSet, daysWeek)) {
    return 'weekdays'
  }
  const daysWeekend = new Set([6, 7])
  if (isEqual(daysSet, daysWeekend)) {
    return 'weekends'
  }
  if (days) {
    return days.map(day => fmtDayStr(day, true)).join(', ')
  }
  return undefined
}

/**
 * A comparison function for saved searches. Can be used to sort them by id.
 */
export const compareBookableShipmentSavedSearch = (
  a: BookableShipmentSavedSearch,
  b: BookableShipmentSavedSearch
): 0 | 1 | -1 => {
  if (a?.id === b?.id) return 0
  if ((a?.id ?? 0) < (b?.id ?? 0)) return -1
  return 1
}

/**
 * Determines whether the content of two saved searches are the same.
 */
export const isEqualBookableShipmentSavedSearch = (
  a: BookableShipmentSavedSearch,
  b: BookableShipmentSavedSearch
): boolean =>
  BookableShipmentSavedSearchFiltersKeys.every(key => isEqual(a[key] || null, b[key] || null))

/**
 * Given a list of saved searches, will return whether the given saved search is in the list.
 */
export const hasBookableShipmentSavedSearch = (
  needle: BookableShipmentSavedSearch,
  haystack: BookableShipmentSavedSearch[]
): boolean => {
  return haystack.some(
    bss =>
      compareBookableShipmentSavedSearch(needle, bss) === 0 ||
      isEqualBookableShipmentSavedSearch(needle, bss)
  )
}

export interface IAvailableTruckDays {
  /**
   * @example '2021-12-01'
   */
  pickupStartDate: string
  totalResultCount: number
  totalUnviewedResultCount: number
  totalTrucksPosted: number
  filteredSearches: BookableShipmentSavedSearch[]
}

/**
 * Returns a new list of total counts for pickupStartDates given.
 */
export const getAvailableTruckDays = (
  days: Moment[],
  savedSearches: BookableShipmentSavedSearch[]
): IAvailableTruckDays[] => {
  const availableTruckDays: IAvailableTruckDays[] = []
  days.forEach(day => {
    const filteredSearches = savedSearches.filter(
      s => s.pickupStartDate === day.format(FORMATS.yearFirst)
    )
    const totalResultCount = filteredSearches.reduce(
      (sum, savedSearch) => sum + (savedSearch?.totalResultCount || 0),
      0
    )
    const totalUnviewedResultCount = filteredSearches.reduce(
      (sum, savedSearch) => sum + (savedSearch?.unviewedResultCount || 0),
      0
    )
    availableTruckDays.push({
      pickupStartDate: day.format(FORMATS.yearFirst),
      totalResultCount,
      totalUnviewedResultCount,
      totalTrucksPosted: filteredSearches.length,
      filteredSearches,
    })
  })
  return availableTruckDays
}

export const getAvailableTruckDaysArg = (totalBaseDays: number, addedDays: number): Moment[] => {
  const availableTruckDaysArg = []

  const now = new Date()
  for (let i = 0; i < totalBaseDays; i += 1) {
    availableTruckDaysArg.push(moment(now).add(addedDays + i, 'days'))
  }

  return availableTruckDaysArg
}

export const isLoadRecommendationsDigestDisabled = (user: IUserData): boolean => {
  const { settings } = user || {}
  return !!(settings?.loadRecommendationsDigest && settings?.loadRecommendationsDigest.length === 0)
}
